import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, distinctUntilChanged } from 'rxjs/operators';
import { NavigationConfigService } from "@ispm/navigation/services/navigationConfig.service";

import { CoreActionCreatorService } from "@ispm/core/services/core-action-creator.service";
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { InfoMenu } from '@ispm/dashboard/menu/interfaces/info-menu';
import { NotificationMessageService } from "@ispm/core/components/customise-notification/notifications-message.service";
import { PanelsService } from '@ispm/layout/services/panels.service';
import { PdfService } from '@ispm/test/services/pdf.service';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { AttachmentService } from '@ispm/migration/services/attachment.service';
import _ from "lodash";
import {WebConfigService} from "@ispm/layout/services/webConfig.service";
import {ModalLoaderNgjsService} from "@ispm/layout/services/modal-loader-ngjs.service";

@Component({
  selector: 'gen-info-menu',
  templateUrl: './info-menu.component.html',
  styleUrls: ['./info-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoMenuComponent implements OnInit {
  @Input() isFromLogin:boolean=false;
  @Input() set webConfig(webConfig: any) {
    this._webConfig = webConfig;
  };
  get webConfig(): any {
    return this._webConfig;
  }
  private panelService: PanelsService;
  public infoMenuList: InfoMenu[];
  public modal:boolean = false;
  
  public supportPanelName: string = "helpAndSupport";
  public supportPanelPosition: string = "right";

  private currentApp: any;
  private _webConfig: any;
  public aboutDialogState;

  constructor(
    _panelService: PanelsService,
    private coreActionCreatorService: CoreActionCreatorService,
    private genomicsStore: Store<IgenomicsState>,
    private notificationMessageService: NotificationMessageService,
    private pdfService: PdfService,
    private userActionCreatorService: UserActionCreatorService,
    private navigationConfigService: NavigationConfigService,
    private workflowService: WorkflowService,
    private attachmentsService: AttachmentService,
    private webConfigService: WebConfigService,
    private modalLoader: ModalLoaderNgjsService
  ) {
    this.panelService = _panelService;
    genomicsStore.select('navigation', 'currentApp')
      .pipe(distinctUntilChanged())
      .subscribe((app) => {
        if (app !== null) {
          this.currentApp = app;
        }
      });
  }

  public openSupportPanel() {
    this.modal = true;

    return this.panelService.openHelpAndSupportPanel(this.supportPanelName);
  }

  public openManualIFU() {
    this.userActionCreatorService.getStoredOrganizationConfiguration().subscribe(orgDetails => {
      let lang = orgDetails && orgDetails.language && orgDetails.language.code
        ? orgDetails.language.code
        : 'en-US';

      this.coreActionCreatorService.setLoader(true);
      this.workflowService.getManual(this.currentApp?.id, lang, 'ifu')
        .subscribe(
          (file) => {
            this.pdfService.openPDF(file, 'Genomics Manual', undefined);
          },
          () => {
            this.notificationMessageService.error('Unable to retrieve IFU');
            this.coreActionCreatorService.setLoader(false)
          },
          () => {
            this.coreActionCreatorService.setLoader(false);
          }
        );
    });
  };

  openAboutBox() {
    const Year = new Date().getFullYear()
    // To handle ISPM-ADMIN
    if (this.currentApp != null) {
      this.webConfigService.getCurrentAppLocalization(this.currentApp).take(1).subscribe((reduxcurrentAppaboutContent) => {
        this.workflowService.getAboutBoxContent().then( (aboutContent) =>{
          aboutContent.body.versionAndBuildNumbers.appPublishDate = new Date(aboutContent.body.versionAndBuildNumbers.appPublishDate);

          if (reduxcurrentAppaboutContent != null && reduxcurrentAppaboutContent.aboutBoxContent != null) { // based on AppConfiguration
            reduxcurrentAppaboutContent.versionAndBuildNumbers = aboutContent.body.versionAndBuildNumbers;
            reduxcurrentAppaboutContent.refNumber = reduxcurrentAppaboutContent.aboutBoxContent.refNumber || aboutContent.body.refNumber;
            reduxcurrentAppaboutContent.Year = Year;
            this.showAboutBoxDialog(reduxcurrentAppaboutContent);
          } else {
            aboutContent.body.aboutBoxContent.Year = Year;
            this.showAboutBoxDialog(aboutContent.body); // Backward comparability
          }
        }).catch((error) => {
          this.notificationMessageService.error('Unable to retrieve "About" content');
          console.log(error);
        });
      })
    } else {
      // Data is  copied from webconfig of  ISPM admin to aboutcontent
      this.workflowService.getAboutBoxContent().then((aboutContent) => {
        aboutContent.body.versionAndBuildNumbers.appPublishDate = new Date(aboutContent.body.versionAndBuildNumbers.appPublishDate);
        aboutContent.body.aboutBoxContent.BuildId = _.get(this.webConfig, 'aboutBoxContent.BuildId');
        aboutContent.body.aboutBoxContent.LegalText = _.get(this.webConfig, 'aboutBoxContent.LegalText');
        aboutContent.body.aboutBoxContent.RX = _.get(this.webConfig, 'aboutBoxContent.RX');
        aboutContent.body.aboutBoxContent.Lot = _.get(this.webConfig, 'aboutBoxContent.Lot');
        aboutContent.body.aboutBoxContent.Subtitle = _.get(this.webConfig, 'aboutBoxContent.Subtitle');
        aboutContent.body.aboutBoxContent.Title = _.get(this.webConfig, 'aboutBoxContent.Title');
        aboutContent.body.aboutBoxContent.evaluationImage = _.get(this.webConfig, 'aboutBoxContent.evaluationImage');
        aboutContent.body.aboutBoxContent.Quantity = _.get(this.webConfig, 'aboutBoxContent.Quantity');
        const str = aboutContent.body.aboutBoxContent.Text;
        aboutContent.body.aboutBoxContent.Text = str.replace(/\d{4}/, Year);
        this.showAboutBoxDialog(aboutContent.body);
        aboutContent.body.Year = Year;
      }).catch( (error)=> {
        this.notificationMessageService.error('Unable to retrieve "About" content');
        console.log(error);
      });
    }

  }

  showAboutBoxDialog(aboutContent) {
    let modalPayload = {};
    modalPayload = aboutContent;
    modalPayload['hideHeader'] = true;
    modalPayload['backdropAllowed'] = false;
    this.modalLoader.popupModal('aboutbox',
      {
        payload: modalPayload
      }, 'NONE').subscribe((data) => {
      // do nothing
    });

  }


  ngOnInit(): void {
    this.infoMenuList = [
      {"icon": "icon__info", "title": "About", "callback": this.openAboutBox, "displayInLogin": false},
      {"icon": "icon__QuestionMark", "title": "IFU", "callback": this.openManualIFU, "displayInLogin": false},
      {"icon": "icon__Support", "title": "Contact us", "callback": this.openSupportPanel, "displayInLogin": true}
    ];
  }

  public generateFunc(val: string) {
    let infoMenu: InfoMenu = this.infoMenuList.find(infoMenu => infoMenu.title == val);
    if(infoMenu != undefined && infoMenu.callback !== undefined) {
      infoMenu.callback.call(this);
    }
  }

  public closeModal(){
    this.modal = false;
    this.panelService.hideLayout(this.supportPanelName);
  }
}
